/**
 * analysisBridge — hand-off signal between the MCAP approval card and the
 * copilot runtime adapter.
 *
 * After a triage run suspends for MCAP approval, the adapter's generator
 * stays alive inside the SAME assistant turn and waits here. The approval
 * card (which tracks upload jobs live via the recording-status SSE stream)
 * fires the signal once every segment of the extraction settles, letting the
 * adapter continue that turn with the deep-analysis stream instead of
 * starting a new message.
 */

type Settlement = { ok: boolean };

const waiters = new Map<string, Array<(s: Settlement) => void>>();
const settled = new Map<string, Settlement>();

/** Called by the MCAP approval card when the extraction batch settles. */
export function notifyUploadSettled(threadId: string, ok: boolean): void {
  if (!threadId) return;
  settled.set(threadId, { ok });
  const pending = waiters.get(threadId);
  if (pending) {
    waiters.delete(threadId);
    pending.forEach((resolve) => resolve({ ok }));
  }
}

/** Clears a recorded settlement (thread switched / new run started). */
export function clearUploadSettled(threadId: string): void {
  settled.delete(threadId);
}

/**
 * Resolves `{ok}` when the extraction for `threadId` settles, or `null` if
 * the run is aborted first. Consumes an already-recorded settlement so the
 * card and the adapter cannot race each other.
 */
export function waitForUploadSettled(
  threadId: string,
  signal?: AbortSignal,
): Promise<Settlement | null> {
  const existing = settled.get(threadId);
  if (existing) {
    settled.delete(threadId);
    return Promise.resolve(existing);
  }
  if (signal?.aborted) return Promise.resolve(null);

  return new Promise((resolve) => {
    const entry = (s: Settlement) => {
      signal?.removeEventListener("abort", onAbort);
      resolve(s);
    };
    const onAbort = () => {
      const pending = waiters.get(threadId);
      if (pending) {
        const idx = pending.indexOf(entry);
        if (idx >= 0) pending.splice(idx, 1);
        if (pending.length === 0) waiters.delete(threadId);
      }
      resolve(null);
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    const list = waiters.get(threadId) ?? [];
    list.push(entry);
    waiters.set(threadId, list);
  });
}
